'use strict';

class ConsentManager {
    constructor(config) {
        this._cookieName = 'user_cookie_consent';
        this._version = config.version || 0;
        this._isAuthenticated = config.isAuthenticated || false;
        this._openedFromBanner = false;

        this._banner = document.getElementById('consent-banner');
        this._gpcBanner = document.getElementById('gpc-banner');
        this._modal = document.getElementById('consent-modal');
        this._functionalBar = document.getElementById('functional-notice-bar');
        this._functionalPopup = document.getElementById('functional-notice-popup');
        this._activeFunctionalNotice = null;
        this._onFunctionalOutsideClick = null;
    }

    // ─── Public API ────────────────────────────────────────────────────────────

    async init() {
        if (navigator.globalPrivacyControl === true) {
            const gpcPrefs = this._buildGpcPrefs();
            this._writeCookie(gpcPrefs);
            this._pushConsentState(gpcPrefs);
            this._applyGpcToModal();
            if (sessionStorage.getItem('gpc-dismissed') !== 'true') {
                this._showGpcBanner();
            }
            return;
        }

        const pendingNotice = sessionStorage.getItem('functional-notice');
        if (pendingNotice) {
            sessionStorage.removeItem('functional-notice');
            this._showFunctionalNotice(pendingNotice);
        }

        const existing = this._readCookie();

        if (existing !== null && existing.v === this._version) {
            return;
        }
        this._clearCookie();

        if (this._isAuthenticated) {
            try {
                const response = await axios.get('/api/cookie-consent', { timeout: 5000 });
                if (response.status === 200 && response.data) {
                    const prefs = {
                        v: response.data.v,
                        source: 'Database',
                        necessary: true,
                        functional: response.data.functional,
                        analytics: response.data.analytics,
                        performance: response.data.performance,
                        advertising: response.data.advertising
                    };
                    this._writeCookie(prefs);
                    this._pushConsentState(prefs);
                    return;
                }
            } catch (_) {
                // Fall through to banner
            }
        }

        this._showBanner();
    }

    openPreferences(fromBanner) {
        this._openedFromBanner = fromBanner === true;
        if (!this._modal) return;

        this._syncTogglesToCookie();

        if (navigator.globalPrivacyControl === true) {
            this._applyGpcToModal();
        }

        this._modal.removeAttribute('inert');
        this._modal.showModal();

        const firstFocusable = this._modal.querySelector(
            'button, [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    dismissGpcBanner() {
        sessionStorage.setItem('gpc-dismissed', 'true');
        this._hideBannerElement(this._gpcBanner);
    }

    dismissFunctionalNotice() {
        if (this._activeFunctionalNotice) {
            this._activeFunctionalNotice.setAttribute('hidden', '');
            this._activeFunctionalNotice = null;
        }
        if (this._onFunctionalOutsideClick) {
            document.removeEventListener('click', this._onFunctionalOutsideClick, true);
            this._onFunctionalOutsideClick = null;
        }
    }

    acceptAll() {
        const isGpc = navigator.globalPrivacyControl === true;
        const prefs = {
            v: this._version,
            source: 'UserAction',
            necessary: true,
            functional: !isGpc,
            analytics: !isGpc,
            performance: !isGpc,
            advertising: !isGpc
        };
        this._writeCookie(prefs);
        this._callApiFireAndForget(prefs);
        window.location.reload();
    }

    rejectAll(fromBanner) {
        const prefs = {
            v: this._version,
            source: 'UserAction',
            necessary: true,
            functional: false,
            analytics: false,
            performance: false,
            advertising: false
        };
        this._writeCookie(prefs);
        this._callApiFireAndForget(prefs);

        if (fromBanner === true) {
            this._hideBannerElement(this._banner);
            this._showFunctionalNotice('bar');
        } else {
            sessionStorage.setItem('functional-notice', 'popup');
            window.location.reload();
        }
    }

    saveChoices() {
        const prefs = this._readToggles();
        prefs.necessary = true;
        this._writeCookie(prefs);
        this._callApiFireAndForget(prefs);
        if (!prefs.functional) {
            sessionStorage.setItem('functional-notice', 'popup');
        }
        window.location.reload();
    }

    // ─── Private: Banner ───────────────────────────────────────────────────────

    _showBanner() {
        if (this._banner) {
            this._banner.classList.add('is-visible');
        }
        var overlay = document.getElementById('consent-overlay');
        if (overlay) overlay.classList.add('is-visible');
    }

    _showGpcBanner() {
        if (this._gpcBanner) {
            this._gpcBanner.classList.add('is-visible');
        }
    }

    _showFunctionalNotice(variant) {
        const el = variant === 'popup' ? this._functionalPopup : this._functionalBar;
        if (!el) return;
        this.dismissFunctionalNotice();
        el.removeAttribute('hidden');
        this._activeFunctionalNotice = el;
        // Defer binding so the click that opened the notice doesn't immediately close it.
        setTimeout(() => {
            if (this._activeFunctionalNotice !== el) return;
            this._onFunctionalOutsideClick = (e) => {
                if (this._activeFunctionalNotice && !this._activeFunctionalNotice.contains(e.target)) {
                    this.dismissFunctionalNotice();
                }
            };
            document.addEventListener('click', this._onFunctionalOutsideClick, true);
        }, 0);
    }

    _hideBannerElement(el) {
        if (el) {
            el.classList.remove('is-visible');
        }
        var overlay = document.getElementById('consent-overlay');
        if (overlay) overlay.classList.remove('is-visible');
    }

    // ─── Private: Modal ────────────────────────────────────────────────────────

    _closePreferences() {
        if (!this._modal) return;
        this._modal.setAttribute('inert', '');

        const hasValidCookie = this._hasValidCookie();
        if (this._openedFromBanner === true && !hasValidCookie) {
            this._showBanner();
        }
        this._openedFromBanner = false;
    }

    _syncTogglesToCookie() {
        const existing = this._readCookie();
        const prefs = (existing && existing.v === this._version) ? existing : null;

        ['functional', 'analytics', 'performance', 'advertising'].forEach(cat => {
            const toggle = document.getElementById('toggle-' + cat);
            if (toggle) {
                toggle.checked = prefs ? Boolean(prefs[cat]) : false;
            }
        });
    }

    _readToggles() {
        const cats = ['functional', 'analytics', 'performance', 'advertising'];
        const prefs = {
            v: this._version,
            source: 'UserAction',
            necessary: true
        };
        cats.forEach(cat => {
            const toggle = document.getElementById('toggle-' + cat);
            prefs[cat] = toggle ? toggle.checked : false;
        });
        return prefs;
    }

    _applyGpcToModal() {
        ['functional', 'analytics', 'performance', 'advertising'].forEach(cat => {
            const toggle = document.getElementById('toggle-' + cat);
            const notice = document.getElementById('gpc-notice-' + cat);
            if (toggle) {
                toggle.checked = false;
                toggle.disabled = true;
            }
            if (notice) {
                notice.removeAttribute('hidden');
            }
        });

        const acceptAllBtn = document.getElementById('consent-accept-all');
        if (acceptAllBtn) {
            acceptAllBtn.disabled = true;
            acceptAllBtn.setAttribute('title', 'Your browser\u2019s Global Privacy Control signal prevents accepting all cookies.');
        }

        const rejectAllBtn = document.getElementById('consent-reject-all');
        if (rejectAllBtn) {
            rejectAllBtn.disabled = true;
            rejectAllBtn.setAttribute('title', 'All optional cookies are already opted out by your Global Privacy Control signal.');
        }

        const gpcFooterNote = document.getElementById('consent-gpc-footer-note');
        if (gpcFooterNote) {
            gpcFooterNote.removeAttribute('hidden');
        }
    }

    // ─── Private: Cookie ───────────────────────────────────────────────────────

    _readCookie() {
        const prefix = this._cookieName + '=';
        const parts = document.cookie.split(';');
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i].trim();
            if (part.startsWith(prefix)) {
                const raw = part.substring(prefix.length);
                try {
                    return JSON.parse(decodeURIComponent(raw));
                } catch (_) {
                    return null;
                }
            }
        }
        return null;
    }

    _writeCookie(prefs) {
        const encoded = encodeURIComponent(JSON.stringify(prefs));
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = this._cookieName + '=' + encoded +
            '; expires=' + expires.toUTCString() +
            '; path=/' +
            '; SameSite=Lax' +
            secure;
    }

    _clearCookie() {
        document.cookie = this._cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }

    _hasValidCookie() {
        const prefs = this._readCookie();
        return prefs !== null && prefs.v === this._version;
    }

    _buildGpcPrefs() {
        return {
            v: this._version,
            source: 'Gpc',
            necessary: true,
            functional: false,
            analytics: false,
            performance: false,
            advertising: false
        };
    }

    // ─── Private: API ──────────────────────────────────────────────────────────

    _callApiFireAndForget(prefs) {
        if (!this._isAuthenticated) return;
        axios.put('/api/cookie-consent', prefs, { timeout: 5000 }).catch(function () {});
    }

    _pushConsentState(prefs) {
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag('consent', 'update', {
            analytics_storage:       prefs.analytics    ? 'granted' : 'denied',
            ad_storage:              prefs.advertising  ? 'granted' : 'denied',
            ad_user_data:            prefs.advertising  ? 'granted' : 'denied',
            ad_personalization:      prefs.advertising  ? 'granted' : 'denied',
            functionality_storage:   prefs.functional   ? 'granted' : 'denied',
            personalization_storage: prefs.performance  ? 'granted' : 'denied',
            security_storage:        'granted'
        });

        window.dataLayer.push({
            event: 'cookie_consent_update',
            consent_functional:  prefs.functional  ? 'granted' : 'denied',
            consent_analytics:   prefs.analytics   ? 'granted' : 'denied',
            consent_performance: prefs.performance ? 'granted' : 'denied',
            consent_advertising: prefs.advertising ? 'granted' : 'denied'
        });
    }
}

// ─── Wire-up after DOM ready ────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', function () {
    var config = window.consentConfig || { version: 0, isAuthenticated: false };
    window.consentManager = new ConsentManager(config);

    var modal = document.getElementById('consent-modal');
    if (modal) {
        modal.addEventListener('close', function () {
            window.consentManager._closePreferences();
        });

        modal.addEventListener('click', function (e) {
            if (e.target === modal) {
                modal.close();
            }
        });

        modal.addEventListener('cancel', function (e) {
            e.preventDefault();
            modal.close();
        });
    }

    document.addEventListener('open-cookie-preferences', function () {
        window.consentManager.openPreferences(false);
    });

    window.consentManager.init();
});
